/*eslint-disable*/

/* This page is a generic page used in 3 cases
1) Teachers getting paired with edifiers
2)Admin updating and viewing edifiers
3)Site-leaders updating and viewing users based on their district */

import React, { useState, useEffect } from 'react';
import { Grid, Container, Typography, Box, Button, CircularProgress, TextField, TablePagination, Checkbox, FormControlLabel, Card, CardContent, ToggleButtonGroup, ToggleButton, Collapse, Badge, Chip } from '@mui/material';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import ViewListIcon from '@mui/icons-material/ViewList';
import FilterListIcon from '@mui/icons-material/FilterList';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';

import toast from 'react-hot-toast';
import { useQueryClient } from 'react-query';
import { Card as EdifierCard } from '../sections/@dashboard/find-edifier';
import { UserDetailedInformationDialog } from '../components/dialog';
// import { CheckinCheckout } from '../components/checkin';
import { getUserDetails, sortByLname } from '../utils';
import { useQueryGetCoachesList, useQueryDeleteCoach, useMutationChangeUserImage, useMutationBatchUpdateAvailability } from '../hooks/react-query/useData';
import { useMutationPairWithEdifier } from '../hooks/react-query/pairing';
import { DeleteDialog, DeleteOperationDialog, AddCoachDialog } from '../components/dialog';
import { TableView, UsersTable } from '../components/table';
import ViewCoachDialog from 'src/components/dialog/ViewCoachDialog';
import EditImage from 'src/components/dialog/EditImage';
import { SelectRole } from '../components/filters';
import { useQueryGetSchoolsList } from '../hooks/react-query/schools';
import { LoadingButton } from '@mui/lab';
import CustomPagination from '../components/pagination/CustomPagination';
import EmptyState from '../components/empty-state';

const Users = () => {
  const userDetails = getUserDetails();

  const pairedUsers = useOutletContext();

  const userRole = userDetails.role.toLowerCase();
  const userToken = userDetails.token;

  const navigate = useNavigate();

  const queryClient = useQueryClient();

  const [open, setOpen] = useState(false);
  const [deleteOperationOpen, setDeleteOperationOpen] = useState(false);
  const [addEdifierOpen, setAddEdifierOpen] = useState(false);
  const [editEdifierOpen, setEditEdifierOpen] = useState(false);
  const [isView, setIsView] = useState(false);
  const [searchedValue, setSearchedValue] = useState('');
  const [coachesRecord, setCoachesRecord] = useState([]);

  const [edifierRankings, setEdifierRankings] = useState({});

  const [imageChangeData, setImageChangeData] = useState('');
  const [previewImage, setPreviewImage] = useState('');


  const [changeImage, setChangeImage] = useState(false);
  const [reviewsGiven, setReviewsGiven] = useState(0);

  const [viewMode, setViewMode] = useState('cards'); // 'cards' or 'table'
  const [filtersOpen, setFiltersOpen] = useState(false);

  const [displayEdifierDetails, setDisplayEdifierDetails] = useState(false);

  const [itemToDeleteId, setItemToDeleteId] = useState('');

  const [idToChangeImage, setIdToChangeImage] = useState('');

  const [currentEdifier, setCurrentEdifier] = useState({});

  const [edifierToEditData, setEdifierToEditData] = useState({});

  const [rankedEdifierPayload, setRankedEdifierPayload] = useState({});

  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Server-side search state
  const [searchQuery, setSearchQuery] = useState('');

  // Filter state for admin and site-leader users
  const [currentSelectedFilter, setCurrentSelectedFilter] = useState('All');
  const [partnershipSiteFilter, setPartnershipSiteFilter] = useState('All');
  const [schools, setSchools] = useState([]);
  const [selectedSchoolId, setSelectedSchoolId] = useState('');

  // Multi-selection state
  const [selectedCards, setSelectedCards] = useState(new Set());
  const [isMultiSelectMode, setIsMultiSelectMode] = useState(false);

  // Multi-selection helper functions (defined early to avoid hoisting issues)
  const clearAllSelections = () => {
    setSelectedCards(new Set());
  };

  const { data: coachesList, isFetching: isLoading } = useQueryGetCoachesList(
    {
      token: userDetails?.token,
      role: userDetails?.role.toLowerCase(),
      page: page + 1, // API uses 1-based pagination
      limit: rowsPerPage,
      ...((userRole === 'admin' || userRole === 'site-leader') && searchQuery && { search: searchQuery }), // Add search parameter for admin and site-leader
      ...((userRole === 'admin' || userRole === 'site-leader') && currentSelectedFilter !== 'All' && {
        userRole: currentSelectedFilter === 'Edifiers' ? 'COACH' :
          currentSelectedFilter === 'Teachers' ? 'TEACHER' :
            currentSelectedFilter === 'Site Leaders' ? 'SITE-LEADER' : null
      }), // Add role filter for admin and site-leader
      ...(userRole === 'admin' && partnershipSiteFilter !== 'All' && selectedSchoolId && {
        schoolId: selectedSchoolId
      }) // Add partnership site filter for admin only
    },
    navigate,
    toast
  );


  const { data: schoolList } = useQueryGetSchoolsList();

  const { isLoading: isSendingPairRequest, mutate: pairWithEdifier } = useMutationPairWithEdifier(
    toast,
    userDetails?.token,
    navigate,
    // searchParams.get('id'),
    queryClient,
    rankedEdifierPayload,
    setRankedEdifierPayload
  );

  const { isLoading: isDeletingCoach, refetch: deleteCoach } = useQueryDeleteCoach(
    { token: userDetails?.token, _id: itemToDeleteId },
    navigate,
    setDeleteOperationOpen,
    toast,
    queryClient,
    userRole
  );
  const updateImageMutation = useMutationChangeUserImage(
    toast,
    userToken,
    navigate,
    setChangeImage,
    setPreviewImage,
    queryClient
  );

  const batchUpdateAvailabilityMutation = useMutationBatchUpdateAvailability(
    toast,
    userToken,
    navigate,
    queryClient,
    clearAllSelections
  );
  useEffect(() => {
    if (checkIfRecordIsNotNullFromBe(coachesList)) {
      setCoachesRecord([]);
    } else {
      // Handle different data structures for different roles
      const users = ['site-leader', 'admin'].includes(userRole)
        ? coachesList?.data?.data?.users
        : coachesList?.data?.data
      setCoachesRecord(users);
    }
  }, [coachesList, userRole]);

  // Reset search when searchQuery changes
  useEffect(() => {
    if ((userRole === 'admin' || userRole === 'site-leader') && !searchQuery) {
      setSearchedValue('');
    }
  }, [searchQuery, userRole]);

  // Populate schools list for admin users only
  useEffect(() => {
    if (userRole === 'admin' && Array.isArray(schoolList?.data?.data)) {
      setSchools([...schoolList.data.data, { _id: 'all', name: 'All' }]);
    } else {
      setSchools([{ _id: 'all', name: 'All' }]);
    }
  }, [schoolList, userRole]);

  useEffect(() => {
    if (Object.keys(rankedEdifierPayload).length > 0) {
      pairWithEdifier();
    }
  }, [rankedEdifierPayload]);

  const checkIfRecordIsNotNullFromBe = (coachesList) => {
    // Handle different data structures for different roles
    const users = userRole === 'site-leader'
      ? coachesList?.data?.data?.users || coachesList?.data?.data
      : coachesList?.data?.data?.users;

    return (
      users?.includes(null) &&
      JSON.stringify(users).split(null)?.length - 1 === users?.length
    );
  };

  const handleClose = () => {
    setOpen(false);
  };
  const handleDeleteOperationClose = () => {
    setDeleteOperationOpen(false);
  };

  const handleAddCoachClose = () => {
    setAddEdifierOpen(false);
    setEditEdifierOpen(false);
    setEdifierToEditData({});
  };

  // Pagination handlers
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Clear search function
  const clearSearch = () => {
    setSearchedValue('');
    setSearchQuery('');
    setPage(0);
  };

  // Filter change handlers
  const handleRoleFilterChange = (newFilter) => {
    setCurrentSelectedFilter(newFilter);
    setPage(0); // Reset to first page when filter changes
  };

  const handlePartnershipSiteFilterChange = (newFilter) => {
    setPartnershipSiteFilter(newFilter);
    setPage(0); // Reset to first page when filter changes
  };

  const handleSchoolIdChange = (schoolId) => {
    setSelectedSchoolId(schoolId);
  };

  // Multi-selection handlers
  const toggleMultiSelectMode = () => {
    setIsMultiSelectMode(!isMultiSelectMode);
    if (isMultiSelectMode) {
      setSelectedCards(new Set()); // Clear selections when exiting multi-select mode
    }
  };

  const toggleCardSelection = (cardId) => {
    const newSelectedCards = new Set(selectedCards);
    if (newSelectedCards.has(cardId)) {
      newSelectedCards.delete(cardId);
    } else {
      newSelectedCards.add(cardId);
    }
    setSelectedCards(newSelectedCards);
  };

  const selectAllCards = () => {
    if (selectedCards.size === coachesRecord?.length) {
      setSelectedCards(new Set()); // Deselect all
    } else {
      setSelectedCards(new Set(coachesRecord?.map(card => card._id))); // Select all
    }
  };

  const handleSetAvailability = (isAvailable) => {
    const userIds = Array.from(selectedCards);
    if (userIds.length === 0) {
      toast.error('Please select at least one user');
      return;
    }
    batchUpdateAvailabilityMutation.mutate({ userIds, isAvailable });
  };


  const searchFromRecord = () => {
    if (userRole === 'admin' || userRole === 'site-leader') {
      // Server-side search for admin and site-leader users
      setSearchQuery(searchedValue);
      setPage(0); // Reset to first page when searching
    } else {
      // Client-side search for non-admin and non-site-leader users
      if (searchedValue === '') {
        if (checkIfRecordIsNotNullFromBe(coachesList)) {
          setCoachesRecord([]);
        } else {
          setCoachesRecord(coachesList?.data?.data?.users);
        }
        return;
      }
      const filtered = checkIfRecordIsNotNullFromBe(coachesList)
        ? []
        : coachesList?.data?.data?.users.filter((item) => item?.fname?.toLowerCase().includes(searchedValue));

      setCoachesRecord(filtered);
    }
  };


  return (
    <Box>
      <Helmet>
        <title>Edifying Teachers | Edifiers</title>
      </Helmet>

      <Container maxWidth="xl" sx={{ padding: '0 !important' }}>
        <DeleteDialog
          open={open && itemToDeleteId !== ''}
          handleClose={handleClose}
          deleteCoach={deleteCoach}
          setDeleteOperationOpen={setDeleteOperationOpen}
        />
        <DeleteOperationDialog
          open={deleteOperationOpen}
          handleDeleteOperationClose={handleDeleteOperationClose}
          isDeleting={isDeletingCoach}
        />
        <AddCoachDialog
          open={addEdifierOpen || (editEdifierOpen && Object.keys(edifierToEditData)?.length > 0)}
          isEdit={editEdifierOpen}
          handleClose={handleAddCoachClose}
          toast={toast}
          userRole={userRole}
          edifierToEditData={edifierToEditData}
          userToken={userToken}
          setEdifierToEditData={setEdifierToEditData}
          queryClient={queryClient}
        />

        {/* Displaying users tags in a modal */}
        <UserDetailedInformationDialog
          open={displayEdifierDetails}
          handleClose={() => setDisplayEdifierDetails(false)}
          userData={currentEdifier}
        />
        <ViewCoachDialog edifierToEditData={edifierToEditData} isView={isView} handleClose={() => setIsView(false)} />
        <EditImage
          previewImage={previewImage}
          setPreviewImage={setPreviewImage}
          open={changeImage}
          isLoading={updateImageMutation.isLoading}
          handleClose={() => setChangeImage(false)}
          setImageChangeData={setImageChangeData}
          updateImageMutation={updateImageMutation}
          userId={idToChangeImage}
        />
        {/* ── Title row ── */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexDirection: { xs: 'column', sm: 'row' },
            rowGap: '20px',
          }}
        >
          <Box>
            {userRole === 'teacher' && (
              <Typography variant="h3" sx={{ mb: '5px', color: 'text.titles' }}>
                Suggested Edifiers
              </Typography>
            )}
            {userRole === 'admin' && (
              <Typography variant="h3" sx={{ mb: '5px', color: 'text.titles' }}>
                Users
              </Typography>
            )}
            {userRole === 'site-leader' && (
              <Typography variant="h3" sx={{ mb: '5px', color: 'text.titles' }}>
                Manage Users
              </Typography>
            )}
          </Box>

          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            {/* Filters toggle — only roles that have filterable options */}
            {(userRole === 'admin' || userRole === 'site-leader') && (
              <Badge
                badgeContent={
                  (partnershipSiteFilter !== 'All' ? 1 : 0) +
                  (currentSelectedFilter !== 'All' ? 1 : 0)
                }
                color="error"
              >
                <Button
                  variant={filtersOpen ? 'contained' : 'outlined'}
                  color="main"
                  onClick={() => setFiltersOpen((prev) => !prev)}
                  startIcon={<FilterListIcon />}
                  endIcon={filtersOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  sx={{ minWidth: '110px', height: '40px' }}
                >
                  Filters
                </Button>
              </Badge>
            )}

            {userRole === 'admin' && (
              <Button
                variant="contained"
                color="main"
                sx={{ height: '40px', width: 'max-content' }}
                onClick={() => setAddEdifierOpen(true)}
              >
                Add new Edifier
              </Button>
            )}
          </Box>
        </Box>

        {/* ── Collapsible filter panel (admin & site-leader only) ── */}
        {(userRole === 'admin' || userRole === 'site-leader') && (
          <Collapse in={filtersOpen}>
            <Box
              sx={{
                mt: 2,
                p: 2,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                backgroundColor: 'background.paper',
                display: 'flex',
                flexWrap: 'wrap',
                gap: 3,
                alignItems: 'flex-end',
              }}
            >
              {/* Partnership Site — admin only */}
              {userRole === 'admin' && schools.length > 0 && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.75rem', fontWeight: 500 }}>
                    Partnership Site
                  </Typography>
                  <SelectRole
                    allRoles={schools}
                    currentSelectedFilter={partnershipSiteFilter}
                    setCurrentSelectedFilter={handlePartnershipSiteFilterChange}
                    setSelectedId={handleSchoolIdChange}
                  />
                </Box>
              )}

              {/* Role — admin & site-leader */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.75rem', fontWeight: 500 }}>
                  Role
                </Typography>
                <SelectRole
                  allRoles={[
                    { _id: 0, name: 'All' },
                    { _id: 1, name: 'Edifiers' },
                    { _id: 2, name: 'Teachers' },
                    { _id: 3, name: 'Site Leaders' },
                  ]}
                  currentSelectedFilter={currentSelectedFilter}
                  setCurrentSelectedFilter={handleRoleFilterChange}
                  setSelectedId={() => {}}
                />
              </Box>

              {/* Multi-select mode toggle — admin & site-leader */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.75rem', fontWeight: 500 }}>
                  Selection Mode
                </Typography>
                <Button
                  variant={isMultiSelectMode ? 'contained' : 'outlined'}
                  color={isMultiSelectMode ? 'warning' : 'main'}
                  onClick={toggleMultiSelectMode}
                  sx={{ height: '40px', minWidth: '130px' }}
                >
                  {isMultiSelectMode ? 'Exit Select' : 'Select Multiple'}
                </Button>
              </Box>

              {/* Active filter chips + clear-all */}
              {(partnershipSiteFilter !== 'All' || currentSelectedFilter !== 'All') && (
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', ml: 'auto' }}>
                  {partnershipSiteFilter !== 'All' && (
                    <Chip
                      label={`Site: ${partnershipSiteFilter}`}
                      size="small"
                      onDelete={() => { setPartnershipSiteFilter('All'); setSelectedSchoolId(''); setPage(0); }}
                    />
                  )}
                  {currentSelectedFilter !== 'All' && (
                    <Chip
                      label={`Role: ${currentSelectedFilter}`}
                      size="small"
                      onDelete={() => { setCurrentSelectedFilter('All'); setPage(0); }}
                    />
                  )}
                  <Button
                    size="small"
                    variant="text"
                    color="error"
                    onClick={() => {
                      setPartnershipSiteFilter('All');
                      setSelectedSchoolId('');
                      setCurrentSelectedFilter('All');
                      setPage(0);
                    }}
                  >
                    Clear all
                  </Button>
                </Box>
              )}
            </Box>
          </Collapse>
        )}
        {/* ── Search & view-toggle row ── */}
        <Box
          sx={{
            display: 'flex',
            mt: '23.63px',
            justifyContent: userRole === 'teacher' ? 'flex-end' : 'space-between',
            flexDirection: { xs: 'column', md: 'row' },
            rowGap: '20px',
            alignItems: 'center',
          }}
        >
          {userRole !== 'teacher' && (
            <Box sx={{
              display: 'flex',
              columnGap: '17px',
              flexDirection: { xs: 'column', md: 'row' },
              rowGap: '20px',
              alignItems: 'center',
            }}>
              <TextField
                placeholder="Search Users by Name"
                className="filters"
                size="small"
                value={searchedValue}
                sx={{
                  minWidth: '300px',
                  maxWidth: { xs: '100%', md: '300px' },
                  '& .MuiInputBase-root': { height: '40px' },
                  '& .MuiInputBase-input': { height: '40px', py: 0 },
                }}
                onChange={(e) => setSearchedValue(e.target.value.toLowerCase())}
                onKeyPress={(e) => { if (e.key === 'Enter') searchFromRecord(); }}
              />

              <Button
                variant="contained"
                color="main"
                size="small"
                onClick={() => searchFromRecord()}
                sx={{ width: 'max-content', height: '40px', minHeight: '40px', lineHeight: 1, display: 'flex', alignItems: 'center' }}
              >
                Search
              </Button>

              {(userRole === 'admin' || userRole === 'site-leader') && (searchQuery || searchedValue) && (
                <Button
                  variant="outlined"
                  color="secondary"
                  size="small"
                  onClick={clearSearch}
                  sx={{ width: 'max-content', height: '40px', minHeight: '40px', lineHeight: 1, display: 'flex', alignItems: 'center' }}
                >
                  Clear
                </Button>
              )}
            </Box>
          )}

          {/* View mode toggle */}
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(e, newMode) => newMode && setViewMode(newMode)}
            aria-label="view mode"
            size="small"
            sx={{
              height: '40px',
              '& .MuiToggleButton-root': {
                px: 2,
                border: '1px solid rgba(86, 156, 211, 0.3)',
                '&.Mui-selected': {
                  background: 'linear-gradient(135deg, #569cd3 0%, #7DB8E5 100%)',
                  color: 'white',
                  '&:hover': { background: 'linear-gradient(135deg, #3A7BA8 0%, #569cd3 100%)' },
                },
              },
            }}
          >
            <ToggleButton value="cards" aria-label="card view">
              <ViewModuleIcon sx={{ mr: 0.5 }} />
              Cards
            </ToggleButton>
            <ToggleButton value="table" aria-label="table view">
              <ViewListIcon sx={{ mr: 0.5 }} />
              Table
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {/* Action buttons for selected cards */}
        {isMultiSelectMode && selectedCards.size > 0 && (
          <Box sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            p: 2,
            backgroundColor: '#f5f5f5',
            borderRadius: 1,
            mt: 2,
            mb: 2
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 'bold' }}>
                {selectedCards.size} card{selectedCards.size !== 1 ? 's' : ''} selected
              </Typography>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={selectedCards.size === coachesRecord?.length && coachesRecord?.length > 0}
                    indeterminate={selectedCards.size > 0 && selectedCards.size < coachesRecord?.length}
                    onChange={selectAllCards}
                  />
                }
                label="Select All"
              />
            </Box>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="outlined"
                color="error"
                onClick={clearAllSelections}
                disabled={selectedCards.size === 0}
              >
                Clear Selection
              </Button>
              <LoadingButton
                variant="contained"
                color="success"
                loading={batchUpdateAvailabilityMutation.isLoading}
                disabled={batchUpdateAvailabilityMutation.isLoading || selectedCards.size === 0}
                onClick={() => handleSetAvailability(true)}
                sx={{ minWidth: '140px' }}
              >
                Set Available
              </LoadingButton>
              <LoadingButton
                variant="contained"
                color="warning"
                loading={batchUpdateAvailabilityMutation.isLoading}
                disabled={batchUpdateAvailabilityMutation.isLoading || selectedCards.size === 0}
                onClick={() => handleSetAvailability(false)}
                sx={{ minWidth: '140px' }}
              >
                Set Unavailable
              </LoadingButton>
            </Box>
          </Box>
        )}
        {userRole !== 'admin' && userRole !== 'site-leader' && coachesRecord?.length !== 0 && (
          <>
            <Typography variant="h5" sx={{ mt: '7.95px', lineHeight: '50px', fontWeight: 600 }}>
              Highest Rated Match
            </Typography>
            <Typography sx={{ fontWeight: 500 }}>
              Our large community of Edifiers has been narrowed down to these three stellar humans. We have selected
              them based on several factors that we know will make them a wonderful mentoring fit for you.
              <br /> Now it's your turn to tell us who you would like to work with. Please review the profiles of each
              suggested Edifier below to get to know them. After you have reviewed their profiles, please tell us who
              you think your best match is. Rank each Edifier and save your rankings. Then we will do our best to pair
              you with your highest ranked available Edifier!{' '}
            </Typography>
          </>
        )}
        {isLoading && (
          <Box sx={{ textAlign: 'center', mt: '30px' }}>
            <CircularProgress />
          </Box>
        )}

        {coachesRecord?.length === 0 && !isLoading && viewMode === 'cards' && (
          <EmptyState
            icon={PersonSearchIcon}
            title="No Matching Edifier Found"
            description="We couldn't find any edifiers matching your search criteria."
            iconClassName="edifier-icon"
            dotsClassName="edifier-dots"
          />

        )}

        {viewMode === 'cards' && (
          <>
            <Grid container spacing={'28.47px'} sx={{ mt: '31px' }}>
              {coachesRecord &&
                sortByLname(coachesRecord)?.map(
                  (list, index) =>
                    list && (
                      <EdifierCard
                        key={index}
                        data={list}
                        index={index}
                        setOpen={setOpen}
                        setItemToDeleteId={setItemToDeleteId}
                        setEdifierToEditData={setEdifierToEditData}
                        setEditEdifierOpen={setEditEdifierOpen}
                        userRole={userDetails?.role.toLowerCase()}
                        setDisplayUserDetails={setDisplayEdifierDetails}
                        searchedValue={searchedValue}
                        setCurrentUser={setCurrentEdifier}
                        setReviewsGiven={setReviewsGiven}
                        reviewsGiven={reviewsGiven}
                        setEdifierRankings={setEdifierRankings}
                        edifierRankings={edifierRankings}
                        setRankedEdifierPayload={setRankedEdifierPayload}
                        rankedEdifierPayload={rankedEdifierPayload}
                        isSendingPairRequest={isSendingPairRequest}
                        notPaired={JSON.stringify(pairedUsers[6]?.data?.data)?.includes(list?._id) === false}
                        rank
                        setIsView={setIsView}
                        setChangeImage={setChangeImage}
                        setIdToChangeImage={setIdToChangeImage}
                        setImageChangeData={setImageChangeData}
                        // Multi-selection props
                        isMultiSelectMode={isMultiSelectMode}
                        isSelected={selectedCards.has(list._id)}
                        onToggleSelection={() => toggleCardSelection(list._id)}
                      />
                    )
                )}
            </Grid>

            {/* Pagination Controls for Grid View */}
            {coachesRecord && coachesRecord?.length > 0 && (
              <Box sx={{ mt: '20px' }}>
                <CustomPagination
                  count={coachesList?.data?.data?.pagination?.totalCount || coachesRecord?.length}
                  page={page}
                  onPageChange={handleChangePage}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  rowsPerPageOptions={[5, 10, 25, 50]}
                  labelRowsPerPage="Profiles per page:"
                  labelDisplayedRows={({ from, to, count }) =>
                    `${from}-${to} of ${count !== -1 ? count : `more than ${to}`}`
                  }
                />
              </Box>
            )}
          </>
        )}

        {viewMode === 'table' && (
          <>
            <UsersTable
              tableData={coachesRecord}
              userRole={userDetails?.role.toLowerCase()}
              setItemToDeleteId={setItemToDeleteId}
              setOpen={setOpen}
              setEditData={setEdifierToEditData}
              setEditOpen={setEditEdifierOpen}
              setIsView={setIsView}
            />

            {/* Pagination Controls for Table View */}
            {coachesRecord && coachesRecord?.length > 0 && (
              <Box sx={{ mt: '20px' }}>
                <CustomPagination
                  count={coachesList?.data?.data?.pagination?.totalCount || coachesRecord?.length}
                  page={page}
                  onPageChange={handleChangePage}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  rowsPerPageOptions={[5, 10, 25, 50]}
                  labelRowsPerPage="Profiles per page:"
                  labelDisplayedRows={({ from, to, count }) =>
                    `${from}-${to} of ${count !== -1 ? count : `more than ${to}`}`
                  }
                />
              </Box>
            )}
          </>
        )}
      </Container>
    </Box>
  );
};

export default Users;
